import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { User, AuthContext as AuthContextType, AppPermission } from '@bapecom/types';

// Store CF email globally so other components can use it
let globalCFEmail: string | null = null;

export const getCFEmail = () => globalCFEmail;

// Create authenticated fetch function
export const createAuthFetch = (baseUrl: string) => {
  return async (endpoint: string, options: RequestInit = {}) => {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    };
    
    if (globalCFEmail) {
      (headers as Record<string, string>)['x-cf-email'] = globalCFEmail;
    }
    
    return fetch(`${baseUrl}${endpoint}`, {
      ...options,
      headers,
      credentials: 'include',
    });
  };
};

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
  apiBaseUrl?: string;
}

/**
 * Get CF Access identity from the endpoint
 */
const getCFAccessIdentity = async (): Promise<{ email: string } | null> => {
  try {
    // Correct path without leading dot
    const res = await fetch('/cdn-cgi/access/get-identity', { credentials: 'include' });
    console.log('CF Identity response:', res.status);
    if (res.ok) {
      const data = await res.json();
      console.log('CF Identity data:', data);
      return { email: data.email };
    }
  } catch (e) {
    console.log('CF Access identity error:', e);
  }
  return null;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ 
  children, 
  apiBaseUrl = '/api' 
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [permissions, setPermissions] = useState<AppPermission[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      try {
        // Step 1: Get email from CF Access
        const cfIdentity = await getCFAccessIdentity();
        
        // Store email globally
        if (cfIdentity?.email) {
          globalCFEmail = cfIdentity.email;
        }
        
        const headers: HeadersInit = {
          'Content-Type': 'application/json',
        };
        
        // Pass CF email to API via header
        if (cfIdentity?.email) {
          headers['x-cf-email'] = cfIdentity.email;
        } else {
          // Dev fallback
          headers['x-dev-email'] = 'admin@bapecom.com';
        }

        const response = await fetch(`${apiBaseUrl}/auth/me`, { 
          headers,
          credentials: 'include',
        });
        
        if (response.ok) {
          const data = await response.json();
          setUser(data.user);
          setPermissions(data.permissions || []);
        } else {
          // Not authenticated
          console.warn('Auth failed:', response.status);
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, [apiBaseUrl]);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    permissions,
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto" />
          <p className="mt-4 text-gray-600">Đang tải...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within AuthProvider');
  }
  return context;
};
